import { useEffect } from 'react';
import { Drawer } from 'react-daisyui';
import { useParams, useSearchParams } from 'react-router-dom';
import { bankDetailsService } from '../../reducers/payments/bankDetailSlice';
import { workspaceService } from '../../reducers/workspaces/workspaceSlice';
import { useAppDispatch, useTypedSelector } from '../../stateStore';
import { isLoadingState } from '../../utils/helpers/common';
import { AppLoader } from '../Loader';
import AirwallexBeneficiaryForm from './AirwallexBeneficiaryForm';

interface IBankDetailSideOverlayProps extends React.PropsWithChildren {
  /**
   * Whether the current user may add a *new* bank account -- derived once in
   * `useVisibleBankDetails` (`canAddBankAccount`) and passed down by callers
   * that know the flag/account-count state. Defaults to `true` so the two
   * deprecating surfaces that don't pass it (StaffLayout, ManageWorkspaceTabLayout)
   * keep today's behaviour unchanged. Only gates the *add* form -- editing an
   * existing account (`?bankAccountForm=<id>`) is unaffected regardless of
   * this prop, since the multi-bank flag never restricts editing.
   */
  canAddBankAccount?: boolean;
  isReadOnly?: boolean;
}

const BankDetailSideOverlay = ({
  canAddBankAccount = true,
  children,
  isReadOnly,
}: IBankDetailSideOverlayProps) => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const { workspaceId } = useParams<{ workspaceId: string }>();

  const isLoading = useTypedSelector(workspaceService.selectors.isLoading);

  const isFindOneLoading = useTypedSelector(
    bankDetailsService.selectors.isFindOneLoading,
  );

  const bankDetail = useTypedSelector(bankDetailsService.selectors.bankDetail);

  useEffect(() => {
    if (!workspaceId) return;

    dispatch(workspaceService.findOne(workspaceId));
  }, [dispatch, workspaceId]);

  const handleRequest = async (id: string) => {
    if (id === '' || id === 'new') return;

    await dispatch(bankDetailsService.findOne(id));
  };

  const searchValue = searchParams.get('bankAccountForm');
  const isAddNew = searchValue === 'new';
  // Gate only the add form via the URL: the param does double duty ('new' vs.
  // a bank-detail id for editing), and editing must stay available regardless
  // of the flag. Derived during render, not stored, so a flag that resolves
  // after mount is reflected on the very next render instead of leaving a
  // stale sidebar-open value around.
  const isSidebarOpen =
    searchValue !== null && (!isAddNew || canAddBankAccount);

  useEffect(() => {
    if (searchValue === 'new') {
      dispatch(bankDetailsService.actions.resetBankDetailState());

      return;
    }

    if (searchValue && searchValue !== 'new') {
      handleRequest(searchValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('bankAccountForm')]);

  const handleToggle = async () => {
    dispatch(bankDetailsService.actions.resetBankDetailState());

    setSearchParams(
      (current) => {
        current.delete('bankAccountForm');

        return current;
      },
      { replace: true },
    );
  };

  const handleOnSave = async () => {
    if (!workspaceId) return;

    handleToggle();

    await dispatch(
      bankDetailsService.findAll({
        sortBy: 'isDefault:desc',
        workspaceId,
      }),
    );
  };

  if (isLoadingState(isLoading)) {
    return <AppLoader.FlexLoader />;
  }

  return (
    <Drawer
      end
      open={isSidebarOpen}
      side={
        <div className="flex h-full w-[330px] flex-col items-start gap-2.5 bg-sr-white shadow-2xl xsm:w-[400px] md:w-[517px]">
          <div className="flex h-full flex-col items-center self-stretch overflow-auto overflow-x-hidden p-6">
            {isLoadingState(isFindOneLoading) ? (
              <AppLoader.FlexLoader loaderHeight="h-full" />
            ) : (
              <>
                <div className="mt-5 flex w-full items-start">
                  <h2 className="text-neutral-950">
                    {isReadOnly
                      ? 'Bank Account Details'
                      : searchParams.get('bankAccountForm') === 'new'
                        ? 'Add New Bank Account'
                        : 'Edit Bank Account'}
                  </h2>
                </div>
                {isSidebarOpen && (
                  // Wrap in a conditional to avoid loading the enclosed component before the side overlay is open
                  // (without this, the bank form loads hidden even on details/teammates tabs)
                  // Then conditionally load the component based on feature flag...
                  <AirwallexBeneficiaryForm
                    bankDetail={bankDetail || null}
                    isReadOnly={isReadOnly}
                    isWorkspaceSettingsPage
                    onCancelClick={handleToggle}
                    onSaveSuccess={handleOnSave}
                  />
                )}
              </>
            )}
          </div>
        </div>
      }
      onClickOverlay={handleToggle}
    >
      {children}
    </Drawer>
  );
};

export default BankDetailSideOverlay;
